import React from 'react'
import { LocalStorageManager } from '../helper/localStorage'

export const AppUserContext = React.createContext(null)

const STORAGE_KEY_APP_USER_PROFILE = 'appUserProfile'

const DEFAULT_APP_USER_PROFILE = {
  appUserId: 0,
  username: '',
  token: '',
  partnerName: ''
}

const safeGetStorageItem = (key) => {
  try {
    return LocalStorageManager.getItem(key)
  } catch (error) {
    console.error(`AppUserContext: failed to read storage key "${key}"`, error)
    return null
  }
}

const trySetStorageItem = (key, value) => {
  try {
    LocalStorageManager.setItem(key, value)
    return true
  } catch (error) {
    console.error(`AppUserContext: failed to write storage key "${key}"`, error)
    return false
  }
}

const sanitizeAppUserProfile = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return DEFAULT_APP_USER_PROFILE
  }

  return {
    appUserId: typeof value.appUserId === 'number' ? value.appUserId : 0,
    username: typeof value.username === 'string' ? value.username : '',
    token: typeof value.token === 'string' ? value.token : '',
    partnerName: typeof value.partnerName === 'string' ? value.partnerName : ''
  }
}

const isSameAppUserProfile = (prev, next) => {
  return (
    prev.appUserId === next.appUserId &&
    prev.username === next.username &&
    prev.token === next.token &&
    prev.partnerName === next.partnerName
  )
}

const buildInitialState = () => {
  const storedProfile = sanitizeAppUserProfile(safeGetStorageItem(STORAGE_KEY_APP_USER_PROFILE))
  return {
    appUserProfile: storedProfile
  }
}

export const AppUserContextProvider = ({ children }) => {
  const [state, setState] = React.useState(() => buildInitialState())

  const setAppUserProfile = React.useCallback((value) => {
    setState((prev) => {
      const nextValue = typeof value === 'function' ? value(prev.appUserProfile) : value
      const nextProfile = sanitizeAppUserProfile(nextValue)

      if (isSameAppUserProfile(prev.appUserProfile, nextProfile)) {
        return prev
      }

      const isSaved = trySetStorageItem(STORAGE_KEY_APP_USER_PROFILE, nextProfile)
      if (!isSaved) {
        return prev
      }

      return {
        ...prev,
        appUserProfile: nextProfile
      }
    })
  }, [])

  const updateAppUserProfile = React.useCallback(
    (value) => {
      setAppUserProfile((prev) => {
        const patch = typeof value === 'function' ? value(prev) : value
        return {
          ...prev,
          ...(patch || {})
        }
      })
    },
    [setAppUserProfile]
  )

  const contextValue = React.useMemo(() => {
    return {
      appUserProfile: state.appUserProfile,
      setAppUserProfile,
      updateAppUserProfile
    }
  }, [state.appUserProfile, setAppUserProfile, updateAppUserProfile])

  return React.createElement(
    AppUserContext.Provider,
    { value: contextValue },
    children
  )
}

export const useAppUserContext = () => {
  const context = React.useContext(AppUserContext)
  if (!context) {
    throw new Error('useAppUserContext must be used within AppUserContextProvider')
  }
  return context
}
